import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';
import { useRefLookup } from '../../hooks/use-ref-lookup.js';

interface MultiRefParamProps {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  refView: string;
  refLabelField: string;
  refValueField: string;
  disabled?: boolean | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}

export function MultiRefParam({
  id,
  values,
  onChange,
  refView,
  refLabelField,
  refValueField,
  disabled,
  error,
  required,
}: MultiRefParamProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useRefLookup({
    view: refView,
    labelField: refLabelField,
    valueField: refValueField,
    q: query,
    enabled: open,
  });

  function toggle(value: string) {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  }

  const labelMap = Object.fromEntries(data?.items.map((i) => [i.value, i.label]) ?? []);

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        className={cn(
          'min-h-10 w-full rounded-[var(--radius-input)] border px-3 py-2 text-sm cursor-pointer text-left',
          'bg-[var(--color-qup-bg-card-dark)] text-[var(--color-qup-text-dark)]',
          error !== undefined
            ? 'border-[var(--color-qup-error)]'
            : 'border-[var(--color-qup-border-dark)]',
          disabled === true ? 'opacity-50 cursor-not-allowed' : '',
        )}
        aria-controls={`${id}-listbox`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-required={required === true ? true : undefined}
        aria-invalid={error !== undefined ? true : undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        disabled={disabled}
        onClick={() => {
          if (disabled !== true) setOpen((p) => !p);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        {values.length === 0 ? (
          <span className="text-[var(--color-qup-text-muted)]">
            {t('params.search_placeholder')}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {values.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--color-qup-primary)]/15 px-2 py-0.5 text-xs text-[var(--color-qup-primary)]"
              >
                {labelMap[v] ?? v}
                <button
                  type="button"
                  aria-label={`Eliminar ${labelMap[v] ?? v}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(v);
                  }}
                  className="ml-0.5 hover:text-[var(--color-qup-primary-hover)]"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
            'bg-[var(--color-qup-bg-card-dark)] shadow-[var(--shadow-card-hover)]',
          )}
        >
          <div className="p-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('params.search_placeholder')}
              className={cn(
                'w-full rounded-[var(--radius-input)] border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-dark)] px-3 py-1.5 text-sm text-[var(--color-qup-text-dark)] placeholder:text-[var(--color-qup-text-muted)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              )}
            />
          </div>
          <ul id={`${id}-listbox`} className="max-h-48 overflow-y-auto py-1">
            {isLoading && (
              <li className="px-3 py-2 text-sm text-[var(--color-qup-text-muted-dark)]">
                {t('loading')}
              </li>
            )}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <li className="px-3 py-2 text-sm text-[var(--color-qup-text-muted-dark)]">
                {t('params.no_options')}
              </li>
            )}
            {data?.items.map((item) => {
              const selected = values.includes(item.value);
              return (
                <li
                  key={item.value}
                  data-selected={selected}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors duration-150',
                    selected
                      ? 'bg-[var(--color-qup-primary)]/15 text-[var(--color-qup-primary)]'
                      : 'text-[var(--color-qup-text-dark)] hover:bg-[var(--color-qup-bg-dark)]',
                  )}
                  onClick={() => toggle(item.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') toggle(item.value);
                  }}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded-sm border',
                      selected
                        ? 'border-[var(--color-qup-primary)] bg-[var(--color-qup-primary)]'
                        : 'border-[var(--color-qup-border-dark)]',
                    )}
                  >
                    {selected && (
                      <svg
                        className="h-3 w-3 text-white"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  {item.label}
                </li>
              );
            })}
          </ul>
          <div className="border-t border-[var(--color-qup-border-dark)] px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-[var(--color-qup-text-muted-dark)]">
              {values.length} seleccionados
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary-hover)]"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {error !== undefined && (
        <span
          id={`${id}-error`}
          role="alert"
          className="mt-1 block text-xs text-[var(--color-qup-error)]"
        >
          {error}
        </span>
      )}
    </div>
  );
}
